import React from "react";
import { Icon } from "@iconify/react";

/**
 * "If you save this, here is what members are told tonight."
 *
 * 🔴 WHY THIS STANDS BETWEEN THE OPERATOR AND SAVE (founder rule 2026-09-23).
 * On 09-22 two switches that change how pairs behave were flipped the same
 * morning, each tested alone, and nobody looked at what the nightly Curiouzz
 * Promise would then say to members. A dry run the next day showed it about
 * to nudge two members who were the ones waiting. So a save that includes
 * such a switch shows this first: the 6 PM tick and deal, dry-run as things
 * are and again with the change, and only the differences.
 *
 * 🔴 IT NEVER SAYS "NOTHING CHANGES" UNLESS IT KNOWS (review 2026-09-23). The
 * green all-clear appears only when the backend says the tick is unchanged
 * (diff.tickUnchanged): both runs complete, nothing differs, and every switch
 * being changed is one the 6 PM tick actually reads. A switch the tick never
 * reads (vault_lockstep_v1, for one) is named as NOT MODELLED, and what a
 * switch changes outside the tick is listed, because an empty dry run proves
 * nothing about either. Before this, turning the Promise and the deal OFF
 * read "Nothing members are told tonight changes" while the real tick would
 * have dealt 2, closed 2 unopened cards and sent 10 Promise actions.
 * The live tick runs the Promise before the deal, and this preview's deal
 * cannot see the members those closes free, so on a night with closes a
 * switch the deal reads is never all-clear, and the freed are named.
 *
 * It never refuses: once the dry run is back, or has failed, the operator can
 * always save. Save waits only while the dry run is running (usually a few
 * seconds), because the whole point is that the decision is made looking at
 * the consequences.
 */
const Row = ({ children, tone }) => (
  <li className={`mb-4 ${tone === "bad" ? "text-danger-600" : ""}`}>{children}</li>
);

const List = ({ title, items, render, empty = "No change." }) => (
  <div className="mb-16">
    <div className="fw-semibold text-primary-light mb-6">{title}</div>
    {items.length ? (
      <ul className="ps-20 mb-0 text-sm">{items.map((it, i) => <Row key={i} tone={it.truthCheck ? "bad" : null}>{render(it)}</Row>)}</ul>
    ) : (
      <div className="text-sm text-secondary-light">{empty}</div>
    )}
  </div>
);

const PROMISE_MODE = {
  live: "Runs: prompts and quiet closes go out",
  off: "Off: no prompts, no closes",
  outside_window: "Skipped: outside its send window",
};
const CADENCE_MODE = {
  live: "Deals for every member and closes unopened cards",
  testers: "Test accounts only; members get a dry run that changes nothing",
  off: "Off: nothing is dealt and no card is closed",
};
const OUTCOME_LABEL = {
  eligibleMembers: "Members in the deal",
  pairsDealt: "Introductions dealt",
  expiring: "Unopened cards closed",
  alreadyHolding: "Already holding one",
  stillWaiting: "Still waiting",
  revealHeld: "Waiting out a fresh Reveal",
  notDealable: "Not dealable (profile, dormant, paused)",
  nobodyFitted: "Nobody free fits",
  errors: "Deal failed (error)",
  cooling: "Paused (untouched cards)",
  // The new-member minimum (2026-10-08): never inside "Nobody free fits".
  belowNewMemberMinimum: "Nobody at or above the new-member minimum",
};
const PROMISE_COUNT_LABEL = {
  prompts: "Prompts that would go out",
  closes: "Quiet closes",
  held: "Prompts held (one nudge at a time, or the member is away)",
  blocked: "Prompts the truth check would stop",
  capped: "Over tonight's cap",
};

const promiseLine = (p) => {
  const note = p.held ? `  (held: ${p.held})` : p.truthCheck ? `  (${p.truthCheck})` : "";
  if (p.action === "prompt") return `${p.to}: "${p.message}"${note}`;
  if (p.action === "close") return `Close ${p.to}'s introduction with ${p.about}${note}`;
  return `Close ${p.to} and ${p.about} (both quiet)${note}`;
};

const plural = (n, one, many) => (n === 1 ? one : many);

// 🔴 SAY WHAT THE TRUTH CHECK WOULD REALLY DO (review 2026-09-23). This used
// to say "The truth check would stop them" for every untrue nudge, including
// the ones the child marked "the truth check is OFF so it would go". Each
// row's fate comes from the backend (utils/flipPreview fateOf): stopped, goes
// anyway because the check is OFF, goes unchecked because the check failed,
// or held tonight by the nudge gate before the check is ever reached.
const TRUTH_ALERT = {
  goes_untrue: {
    tone: "danger",
    text: (n) =>
      `With this change, ${n} ${plural(n, "nudge", "nudges")} would GO to someone for whom it is not true. ` +
      `The truth check is OFF, so nothing would stop ${plural(n, "it", "them")}.`,
  },
  goes_unchecked: {
    tone: "danger",
    text: (n) =>
      `With this change, the truth check could not be run on ${n} ${plural(n, "nudge", "nudges")}. ` +
      `If it fails the same way tonight, ${plural(n, "it goes", "they go")} out unchecked.`,
  },
  blocked: {
    tone: "danger",
    text: (n) =>
      `With this change, ${n} ${plural(n, "nudge", "nudges")} would be aimed at someone for whom it is not true. ` +
      `The truth check would stop ${plural(n, "it", "them")}, but it means the change points the ladder at the wrong people.`,
  },
  held: {
    tone: "warning",
    text: (n) =>
      `With this change, ${n} ${plural(n, "nudge", "nudges")} would be aimed at someone for whom it is not true. ` +
      `${plural(n, "It is", "They are")} held tonight anyway (one nudge at a time, or the member is away), so nothing is sent, ` +
      `but the change points the ladder at the wrong people.`,
  },
};
const TRUTH_ORDER = ["goes_untrue", "goes_unchecked", "blocked", "held"];

// Rows from a backend that sent only wouldBeBlockedIfFlipped (no fate): read
// the fate from the row's own wording, never assuming it was stopped.
const fateOf = (p) => {
  if (p.fate) return p.fate;
  if (p.held) return "held";
  const t = String(p.truthCheck || "");
  if (/^would be BLOCKED/.test(t)) return "blocked";
  if (/^NOT TRUE/.test(t)) return "goes_untrue";
  return "goes_unchecked";
};

const FlipPreviewPanel = ({ changes, job, onSaveAnyway, onCancel }) => {
  const running = !job || job.status === "running";
  const failed = job && job.status === "failed";
  const r = job && job.result;
  const d = r && r.diff;
  const at = r && r.at ? new Date(r.at).toLocaleString("en-CA", { timeZone: "America/Toronto", weekday: "short", hour: "numeric", minute: "2-digit" }) + " ET" : "the next 6 PM";
  const coverage = (d && d.coverage) || { notModelled: [], alsoChanges: [] };
  const skipped = d && d.skipped === "not_modelled";
  // Only the backend's verdict earns the green: never computed here from
  // empty lists, which is exactly how the false all-clear happened.
  const allClear = !!(d && d.tickUnchanged === true);
  const modes = d && d.modes;
  const modesKnown = modes && modes.promise && modes.cadence && modes.promise.now && modes.cadence.now;
  const pt = d && d.promiseTick;
  const expiriesAdded = (d && d.expiriesAdded) || [];
  const expiriesRemoved = (d && d.expiriesRemoved) || [];
  const notGoingAdded = (d && d.notGoingAdded) || [];
  const notGoingRemoved = (d && d.notGoingRemoved) || [];
  const freed = coverage.freedByPromise || null;
  const truthRows = (d && (d.truthFindingsIfFlipped || d.wouldBeBlockedIfFlipped)) || [];
  const truthByFate = TRUTH_ORDER.map((fate) => ({ fate, rows: truthRows.filter((p) => fateOf(p) === fate) })).filter(
    (g) => g.rows.length
  );
  const untrueEitherWay = (d && d.truthFindingsEitherWay) || [];

  return (
    <div
      className="position-fixed start-0 end-0 bg-base border-top px-24 py-20"
      style={{ bottom: 64, zIndex: 1051, maxHeight: "70vh", overflowY: "auto", boxShadow: "0 -12px 36px rgba(5, 2, 28, 0.18)" }}
      role="dialog"
      aria-label="What members would be told tonight"
    >
      <div className="d-flex align-items-start gap-3 flex-wrap mb-16">
        <Icon icon="mdi:eye-check-outline" className="text-primary-600" style={{ fontSize: 28 }} />
        <div className="flex-grow-1" style={{ minWidth: 240 }}>
          <h6 className="mb-4">Before you save: what members would be told tonight</h6>
          <div className="text-sm text-secondary-light">
            You are changing {changes.map((c) => `${c.name} to ${c.value ? "ON" : "OFF"}`).join(", ")}.
            {" "}This is a dry run of {at}, as things are and with your change. Nothing is sent.
          </div>
        </div>
      </div>

      {running && (
        <div className="text-sm text-secondary-light d-flex align-items-center gap-2 mb-16">
          <span className="spinner-border spinner-border-sm" /> Running tonight twice. Usually a few seconds.
        </div>
      )}

      {failed && (
        <div className="alert alert-warning py-2 px-3 mb-16">
          The preview could not run ({job.error || "unknown error"}). You can still save, but you will be saving without seeing the effect.
        </div>
      )}

      {d && (
        <>
          {d.errors && d.errors.length > 0 && (
            <div className="alert alert-warning py-2 px-3 mb-16">
              Part of the preview failed: {d.errors.join("; ")}. Treat what follows as incomplete.
            </div>
          )}

          {coverage.notModelled.length > 0 && (
            <div className="alert alert-warning py-2 px-3 mb-16">
              <div className="fw-semibold mb-4">
                {skipped
                  ? "This preview cannot show this change. Nothing was run."
                  : "Part of this change is not modelled by this preview."}
              </div>
              <ul className="ps-20 mb-0 text-sm">
                {coverage.notModelled.map((n) => (
                  <li key={n.flag}>
                    <code>{n.flag}</code> is never read at the 6 PM tick. It changes {n.changes}, which a dry run of
                    tonight cannot show. An empty result below proves nothing about it.
                  </li>
                ))}
              </ul>
            </div>
          )}

          {allClear && (
            <div className="alert alert-success py-2 px-3 mb-16">
              Nothing members are told at tonight's 6 PM tick changes.
            </div>
          )}

          {coverage.alsoChanges.length > 0 && (
            <div className="alert alert-info py-2 px-3 mb-16">
              <div className="fw-semibold mb-4">Also changes, outside what this preview shows:</div>
              <ul className="ps-20 mb-0 text-sm">
                {coverage.alsoChanges.map((a) => (
                  <li key={a.flag}>
                    <code>{a.flag}</code>: {a.changes}.
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!skipped && freed && (
            <div className="alert alert-warning py-2 px-3 mb-16">
              <div className="fw-semibold mb-4">This preview cannot see everyone tonight's deal will see.</div>
              <div className="text-sm">
                Tonight the Curiouzz Promise closes {freed.closes} {plural(freed.closes, "introduction", "introductions")} before
                {" "}the 6 PM deal{freed.pairs && freed.pairs.length ? ` (${freed.pairs.join("; ")})` : ""}, which frees up
                {" "}to {freed.members} members. {freed.dealsNow === false
                  ? "The run with this change deals"
                  : freed.dealsFlipped === false
                    ? "The run as things are deals"
                    : "Both runs of this preview deal"} as if those introductions were still open, so
                {" "}what {freed.flags.map((f, i) => (
                  <React.Fragment key={f}>
                    {i > 0 ? ", " : ""}
                    <code>{f}</code>
                  </React.Fragment>
                ))} would do for those members is not shown. Everything else below is complete.
              </div>
            </div>
          )}

          {!skipped &&
            truthByFate.map(({ fate, rows }) => (
              <div key={fate} className={`alert alert-${TRUTH_ALERT[fate].tone} py-2 px-3 mb-16`}>
                <div className="mb-4">{TRUTH_ALERT[fate].text(rows.length)}</div>
                <ul className="ps-20 mb-0 text-sm">
                  {rows.map((p, i) => (
                    <li key={i}>{promiseLine(p)}</li>
                  ))}
                </ul>
              </div>
            ))}

          {!skipped && untrueEitherWay.length > 0 && (
            <div className="text-sm text-secondary-light mb-16">
              {untrueEitherWay.length} {plural(untrueEitherWay.length, "nudge is", "nudges are")} untrue as things are,
              {" "}with or without this change: {untrueEitherWay.map(promiseLine).join("; ")}.
            </div>
          )}

          {!skipped && modesKnown && (
            <div className="mb-16 text-sm">
              <div className="fw-semibold text-primary-light mb-6">How tonight's 6 PM tick would run</div>
              <table className="table table-sm mb-0" style={{ maxWidth: 640 }}>
                <thead><tr><th /><th>Now</th><th>With your change</th></tr></thead>
                <tbody>
                  <tr className={modes.promise.now !== modes.promise.flipped ? "fw-semibold text-danger-600" : ""}>
                    <td>The Curiouzz Promise</td>
                    <td>{PROMISE_MODE[modes.promise.now] || modes.promise.now}</td>
                    <td>{PROMISE_MODE[modes.promise.flipped] || modes.promise.flipped || "-"}</td>
                  </tr>
                  <tr className={modes.cadence.now !== modes.cadence.flipped ? "fw-semibold text-danger-600" : ""}>
                    <td>The 6 PM deal</td>
                    <td>{CADENCE_MODE[modes.cadence.now] || modes.cadence.now}</td>
                    <td>{CADENCE_MODE[modes.cadence.flipped] || modes.cadence.flipped || "-"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {!skipped && (
            <>
              <List title="Nudges and closes that would START" items={d.promiseAdded} render={promiseLine} />
              <List title="Nudges and closes that would STOP" items={d.promiseRemoved} render={promiseLine} />
              {(notGoingAdded.length > 0 || notGoingRemoved.length > 0) && (
                <List
                  title="Aimed at, but would not go tonight either way (held, or stopped by the truth check)"
                  items={[...notGoingAdded.map((x) => ({ ...x, s: "+" })), ...notGoingRemoved.map((x) => ({ ...x, s: "-" }))]}
                  render={(x) => `${x.s === "+" ? "Now aimed at" : "No longer aimed at"}: ${promiseLine(x)}`}
                />
              )}
              <List
                title="6 PM introductions that would change"
                items={[...d.dealsAdded.map((x) => ({ ...x, s: "+" })), ...d.dealsRemoved.map((x) => ({ ...x, s: "-" }))]}
                render={(x) => `${x.s === "+" ? "New" : "No longer"}: ${x.member} and ${x.partner}${x.revived ? " (a second chance)" : ""}`}
              />
              <List
                title="Unopened cards the 6 PM tick would close"
                items={[...expiriesAdded.map((x) => ({ ...x, s: "+" })), ...expiriesRemoved.map((x) => ({ ...x, s: "-" }))]}
                render={(x) => `${x.s === "+" ? "Would now close" : "Would no longer close"}: ${x.a} and ${x.b}`}
              />
            </>
          )}

          {!skipped && pt && pt.now && pt.flipped && (
            <div className="mb-16 text-sm">
              <div className="fw-semibold text-primary-light mb-6">The Curiouzz Promise tonight</div>
              <table className="table table-sm mb-0" style={{ maxWidth: 520 }}>
                <thead><tr><th /><th>Now</th><th>With your change</th></tr></thead>
                <tbody>
                  {Object.keys(PROMISE_COUNT_LABEL).map((k) => (
                    <tr key={k} className={pt.now[k] !== pt.flipped[k] ? "fw-semibold" : ""}>
                      <td>{PROMISE_COUNT_LABEL[k]}</td>
                      <td>{pt.now[k] ?? "-"}</td>
                      <td>{pt.flipped[k] ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!skipped && Object.keys(d.outcomes || {}).length > 0 && (
            <div className="mb-16 text-sm">
              <div className="fw-semibold text-primary-light mb-6">At the 6 PM deal</div>
              <table className="table table-sm mb-0" style={{ maxWidth: 460 }}>
                <thead><tr><th /><th>Now</th><th>With your change</th></tr></thead>
                <tbody>
                  {Object.entries(d.outcomes).map(([k, o]) => (
                    <tr key={k} className={o.now !== o.flipped ? "fw-semibold" : ""}>
                      <td>{OUTCOME_LABEL[k] || k}</td>
                      <td>{o.now ?? "-"}</td>
                      <td>{o.flipped ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* The new-member minimum's what-if (2026-10-08): with the start at
              "now", nobody who can be introduced tonight is covered (members
              outside a Live city are new, but cannot be introduced), so the
              backend also runs tonight with the start 30 days earlier. It
              names the Live-city part only when that run applied it (byCity:
              the cities are recorded for the start; review 2026-10-08). */}
          {!skipped && d.newMemberMinimumWhatIf && (
            <div className="mb-16 text-sm">
              <div className="fw-semibold text-primary-light mb-6">
                {`What if the start had been ${d.newMemberMinimumWhatIf.sinceDays} days earlier (everyone who signed up${
                  d.newMemberMinimumWhatIf.byCity ? ", or first joined a Live city," : ""
                } since then covered)`}
              </div>
              <div className="text-secondary-light mb-6">
                Tonight's introductions as things are: {d.newMemberMinimumWhatIf.pairsDealt.now ?? "-"}. With the minimum
                covering them: {d.newMemberMinimumWhatIf.pairsDealt.whatIf ?? "-"}, of which{" "}
                {d.newMemberMinimumWhatIf.dealsRemoved.length} would not happen and{" "}
                {d.newMemberMinimumWhatIf.dealsAdded.length} would happen instead.{" "}
                {d.newMemberMinimumWhatIf.belowNewMemberMinimum != null
                  ? `${d.newMemberMinimumWhatIf.belowNewMemberMinimum} ${plural(
                      d.newMemberMinimumWhatIf.belowNewMemberMinimum,
                      "member would",
                      "members would"
                    )} be introduced to nobody tonight because of it.`
                  : ""}
              </div>
              {/* Review 2026-10-08: an empty list here must not read as "nothing
                  changes"; most of the effect is at the day doors. */}
              <div className="text-secondary-light mb-6">
                This replays only the 6 PM run. Most of the minimum's effect is during the day (the handover, the
                stage and the first introduction after sign-up), which no preview shows: in the 30 days to 8 October,
                24 of the 29 introductions under 70 with a recent sign-up on them were made during the day. Once it is
                ON, each 6 PM report counts the members it left with nobody during the day.
              </div>
              <List
                title="Set aside (the percentage the card would have shown)"
                items={d.newMemberMinimumWhatIf.setAside || []}
                render={(x) => `${x.member} and ${x.partner}: ${x.shown}%`}
                empty="Nobody would be set aside tonight."
              />
              <List
                title="Introductions that would change"
                items={[
                  ...d.newMemberMinimumWhatIf.dealsAdded.map((x) => ({ ...x, s: "+" })),
                  ...d.newMemberMinimumWhatIf.dealsRemoved.map((x) => ({ ...x, s: "-" })),
                ]}
                render={(x) => `${x.s === "+" ? "New" : "No longer"}: ${x.member} and ${x.partner}`}
              />
              {(d.newMemberMinimumWhatIf.errors || []).length > 0 && (
                <div className="text-danger-600">The what-if did not finish: {d.newMemberMinimumWhatIf.errors.join("; ")}</div>
              )}
            </div>
          )}
        </>
      )}

      <div className="d-flex gap-3 justify-content-end">
        <button type="button" className="btn btn-outline-secondary px-20 py-9 radius-8" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary px-24 py-9 radius-8"
          onClick={onSaveAnyway}
          disabled={running}
        >
          {running ? "Waiting for the preview…" : "Save with this change"}
        </button>
      </div>
    </div>
  );
};

export default FlipPreviewPanel;
